// piano-frontend/src/pages/admin/OperationsBlockSchedule.js
// 운영 설정 — 선생님 스케줄 설정(블록) 탭 (PC 전용). 캘린더 캔버스 방식.
// 차단 시간은 시간 캔버스 위의 둥근 블록(옅은 면 + 왼쪽 컬러 바)으로 표시된다.
// - 빈 곳 드래그: 새 차단 블록 그리기(빗금 + '저장 전' 배지)
// - 블록 위 30분 구간 클릭: 해당 구간만 잘라 해제 예정(회색 빗금 고스트, 클릭 시 복구)
// - 하단 저장 바: 변경 목록 칩(개별 되돌리기) + 일괄 저장
// 저장 시 추가 구간은 POST /block-schedules(서버가 30분 분할), 해제 칸은 행별 DELETE.
// 전체 보기는 겹치는 선생님 블록을 이름표와 함께 나란히 배치한 읽기 전용 개요.
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Stack, Paper, Button, Chip, TextField,
  CircularProgress, Tooltip, Alert
} from "@mui/material";
import { ContentCut, Undo, KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import api from "../../api/axios";
import { useDarkMode } from "../../App";
import { useDialog } from "../../contexts/DialogProvider";

const DAY_CODES = [["MON", "월"], ["TUE", "화"], ["WED", "수"], ["THU", "목"], ["FRI", "금"], ["SAT", "토"]];
const DAY_LABEL = Object.fromEntries(DAY_CODES);

const HALF_H = 26;                 // 30분 = 26px
const HOUR_H = HALF_H * 2;
const AXIS_MIN = 0;
const AXIS_MAX = 24 * 60;

// 선생님별 색: [라이트, 다크] — 옅은 면(fill), 왼쪽 바(bar), 텍스트(text)
// 인덱스 % 길이로 배정하므로 선생님 수가 이 길이를 넘으면 색이 겹친다. 뒤에 추가만 하고
// 기존 순서는 바꾸지 말 것(바꾸면 기존 선생님들의 색이 전부 밀린다).
export const TEACHER_PALETTE = [
  { fill: ["#B5D4F4", "#0C447C"], bar: ["#185FA5", "#378ADD"], text: ["#0C447C", "#B5D4F4"] },
  { fill: ["#9FE1CB", "#085041"], bar: ["#0F6E56", "#5DCAA5"], text: ["#085041", "#9FE1CB"] },
  { fill: ["#CECBF6", "#3C3489"], bar: ["#534AB7", "#7F77DD"], text: ["#26215C", "#CECBF6"] },
  { fill: ["#FAC775", "#633806"], bar: ["#854F0B", "#EF9F27"], text: ["#412402", "#FAC775"] },
  { fill: ["#F4C0D1", "#4B1528"], bar: ["#993556", "#ED93B1"], text: ["#4B1528", "#F4C0D1"] },
  { fill: ["#F5C4B3", "#712B13"], bar: ["#993C1D", "#D85A30"], text: ["#4A1B0C", "#F5C4B3"] },
  { fill: ["#C0DD97", "#27500A"], bar: ["#3B6D11", "#639922"], text: ["#173404", "#C0DD97"] },
  { fill: ["#AFA9EC", "#26215C"], bar: ["#26215C", "#AFA9EC"], text: ["#26215C", "#CECBF6"] },
  { fill: ["#5DCAA5", "#04342C"], bar: ["#04342C", "#5DCAA5"], text: ["#04342C", "#9FE1CB"] },
  { fill: ["#85B7EB", "#042C53"], bar: ["#042C53", "#85B7EB"], text: ["#042C53", "#B5D4F4"] }
];

const toMin = (t) => {
  const [h, m] = String(t).slice(0, 5).split(":").map(Number);
  return h * 60 + m;
};
const minToTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const cellKey = (day, min) => `${day}_${min}`;

export default function OperationsBlockSchedule() {
  const { darkMode } = useDarkMode();
  const { confirm, toast } = useDialog();
  const [blocks, setBlocks] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // 선택 선생님의 30분 칸 상태: key(요일_분) → { status: 'b'(차단중)|'add'(추가예정)|'rem'(해제예정), id?, reason? }
  const [cells, setCells] = useState({});
  const [newReason, setNewReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [axis, setAxis] = useState({ start: 12 * 60, end: 18 * 60 });
  // 빈 캔버스 드래그로 새 블록 그리기: { day, rectTop, anchor, current }
  const [drag, setDrag] = useState(null);
  // 블록 위 호버 중인 30분 구간(가위 표시): { day, min }
  const [hoverSeg, setHoverSeg] = useState(null);
  const axisInitRef = useRef(false);

  const pal = useCallback((idx) => {
    const p = TEACHER_PALETTE[idx % TEACHER_PALETTE.length];
    const m = darkMode ? 1 : 0;
    return { fill: p.fill[m], bar: p.bar[m], text: p.text[m] };
  }, [darkMode]);

  const buildCells = useCallback((rows, teacherId) => {
    const next = {};
    rows.forEach((row) => {
      if (row.teacher_id !== teacherId) return;
      next[cellKey(row.day_of_week, toMin(row.start_time))] = {
        status: "b", id: row.id, reason: row.reason || ""
      };
    });
    return next;
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [blockRes, teacherRes] = await Promise.all([
        api.get("/block-schedules"),
        api.get("/teachers")
      ]);
      const rows = blockRes.data;
      setBlocks(rows);
      setTeachers(teacherRes.data);
      // 시간 축은 최초 1회, 등록된 블록 범위 ±1시간(정시 스냅)으로 산출
      if (!axisInitRef.current) {
        axisInitRef.current = true;
        if (rows.length > 0) {
          const mins = rows.map((r) => toMin(r.start_time));
          const lo = Math.min(...mins) - 60;
          const hi = Math.max(...mins) + 30 + 60;
          setAxis({
            start: Math.max(AXIS_MIN, Math.min(12 * 60, Math.floor(lo / 60) * 60)),
            end: Math.min(AXIS_MAX, Math.max(18 * 60, Math.ceil(hi / 60) * 60))
          });
        }
      }
      return rows;
    } catch (err) {
      console.error("블록 스케줄 조회 실패:", err);
      setBlocks([]);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // 드래그 진행/확정 (30분 스냅, 세로 이동만 추적)
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const slot = Math.floor((e.clientY - drag.rectTop) / HALF_H);
      const min = Math.max(axis.start, Math.min(axis.end - 30, axis.start + slot * 30));
      setDrag((p) => (p && p.current !== min ? { ...p, current: min } : p));
    };
    const onUp = () => {
      setCells((prev) => {
        const next = { ...prev };
        const lo = Math.min(drag.anchor, drag.current);
        const hi = Math.max(drag.anchor, drag.current);
        for (let m = lo; m <= hi; m += 30) {
          const k = cellKey(drag.day, m);
          if (!next[k]) next[k] = { status: "add" };
        }
        return next;
      });
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, axis]);

  let addCount = 0;
  let remCount = 0;
  Object.values(cells).forEach((c) => {
    if (c.status === "add") addCount += 1;
    if (c.status === "rem") remCount += 1;
  });
  const dirty = addCount + remCount > 0;

  const selectTeacher = async (id) => {
    if (id === selectedId) return;
    if (dirty && !(await confirm({
      title: "변경 사항 취소",
      message: "저장하지 않은 차단 변경 사항이 있습니다.\n이동하면 변경 내용이 사라집니다. 계속하시겠습니까?",
      confirmText: "이동",
      color: "warning"
    }))) return;
    setSelectedId(id);
    setCells(id == null ? {} : buildCells(blocks, id));
    setHoverSeg(null);
  };

  // 블록 위 30분 구간 클릭: 차단중 → 해제 예정, 추가 예정 → 취소
  const cutCell = (day, min, type) => {
    const key = cellKey(day, min);
    setCells((prev) => {
      const cur = prev[key];
      if (!cur) return prev;
      if (type === "b" && cur.status === "b") return { ...prev, [key]: { ...cur, status: "rem" } };
      if (type === "add" && cur.status === "add") {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return prev;
    });
  };

  // 해제 예정(고스트) 구간 클릭: 차단중으로 복구
  const restoreKeys = (keys) => {
    setCells((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        if (next[k]?.status === "rem") next[k] = { ...next[k], status: "b" };
      });
      return next;
    });
  };

  const undoChange = (change) => {
    setCells((prev) => {
      const next = { ...prev };
      change.keys.forEach((k) => {
        if (change.type === "add") delete next[k];
        else if (next[k]?.status === "rem") next[k] = { ...next[k], status: "b" };
      });
      return next;
    });
  };

  const resetCells = () => setCells(buildCells(blocks, selectedId));

  async function save() {
    if (selectedId == null) return;
    const removeIds = [];
    const addByDay = {};
    Object.entries(cells).forEach(([key, c]) => {
      if (c.status === "rem") removeIds.push(c.id);
      if (c.status === "add") {
        const sep = key.indexOf("_");
        const day = key.slice(0, sep);
        const min = Number(key.slice(sep + 1));
        (addByDay[day] = addByDay[day] || []).push(min);
      }
    });
    setSaving(true);
    try {
      for (const id of removeIds) {
        await api.delete(`/block-schedules/${id}`);
      }
      for (const [day, mins] of Object.entries(addByDay)) {
        mins.sort((a, b) => a - b);
        // 연속 칸은 하나의 구간으로 묶어 등록(서버가 30분 행으로 분할 저장)
        const ranges = [];
        let start = null;
        let prev = null;
        for (const m of mins) {
          if (start !== null && m !== prev + 30) {
            ranges.push([start, prev + 30]);
            start = null;
          }
          if (start === null) start = m;
          prev = m;
        }
        if (start !== null) ranges.push([start, prev + 30]);
        for (const [rs, re] of ranges) {
          await api.post("/block-schedules", {
            teacher_id: selectedId,
            day_of_week: day,
            start_time: minToTime(rs),
            end_time: minToTime(re),
            reason: newReason || undefined
          });
        }
      }
      toast("블록 스케줄이 저장되었습니다.");
      setNewReason("");
    } catch (err) {
      toast(err?.response?.data?.message || "저장 실패", "error");
    } finally {
      const rows = await fetchAll();
      setCells(selectedId == null ? {} : buildCells(rows, selectedId));
      setSaving(false);
    }
  }

  if (!blocks) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const editable = selectedId != null && !saving;
  const selectedIdx = teachers.findIndex((t) => t.id === selectedId);
  const selectedPal = pal(Math.max(selectedIdx, 0));

  const lineStrong = darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)";
  const lineFaint = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)";
  const stripe = darkMode ? "rgba(0,0,0,0.30)" : "rgba(255,255,255,0.45)";
  const ghostHatch = darkMode ? "rgba(136,135,128,0.28)" : "rgba(136,135,128,0.20)";
  const ghostText = darkMode ? "#B4B2A9" : "#5F5E5A";
  const cutOverlay = darkMode ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.10)";

  const canvasH = ((axis.end - axis.start) / 30) * HALF_H;
  const yOf = (min) => ((min - axis.start) / 30) * HALF_H;
  const colBg = {
    backgroundImage:
      `repeating-linear-gradient(to bottom, ${lineStrong} 0 1px, transparent 1px ${HOUR_H}px), ` +
      `repeating-linear-gradient(to bottom, transparent 0 ${HALF_H}px, ${lineFaint} ${HALF_H}px ${HALF_H + 1}px, transparent ${HALF_H + 1}px ${HOUR_H}px)`
  };

  // 선택 선생님: 요일별로 같은 상태('b'/'add'/'rem')의 연속 구간을 묶는다
  const segmentsOf = (code) => {
    const segs = [];
    let cur = null;
    for (let t = axis.start; t < axis.end; t += 30) {
      const cell = cells[cellKey(code, t)];
      const st = cell?.status || null;
      if (cur && cur.type === st) {
        cur.end = t + 30;
        cur.keys.push(cellKey(code, t));
        if (cell?.reason) cur.reasons.add(cell.reason);
      } else {
        if (cur && cur.type) segs.push(cur);
        cur = {
          type: st, start: t, end: t + 30, keys: [cellKey(code, t)],
          reasons: new Set(cell?.reason ? [cell.reason] : [])
        };
      }
    }
    if (cur && cur.type) segs.push(cur);
    return segs;
  };
  const segByDay = {};
  if (selectedId != null) {
    DAY_CODES.forEach(([code]) => { segByDay[code] = segmentsOf(code); });
  }

  // 하단 변경 목록(연속 구간 단위)
  const changes = [];
  DAY_CODES.forEach(([code]) => {
    (segByDay[code] || []).forEach((seg) => {
      if (seg.type === "add" || seg.type === "rem") {
        changes.push({ type: seg.type, day: code, start: seg.start, end: seg.end, keys: seg.keys });
      }
    });
  });

  // 전체 보기: 요일 → 선생님 인덱스 → 병합 구간 목록
  const overview = {};
  if (selectedId == null) {
    const idxById = {};
    teachers.forEach((t, idx) => { idxById[t.id] = idx; });
    const grouped = {};
    blocks.forEach((row) => {
      const idx = idxById[row.teacher_id];
      if (idx == null) return;
      const day = row.day_of_week;
      ((grouped[day] = grouped[day] || {})[idx] = grouped[day][idx] || []).push({
        min: toMin(row.start_time), reason: row.reason || ""
      });
    });
    Object.entries(grouped).forEach(([day, byIdx]) => {
      overview[day] = {};
      Object.entries(byIdx).forEach(([idx, slots]) => {
        slots.sort((a, b) => a.min - b.min);
        const runs = [];
        slots.forEach((s) => {
          const last = runs[runs.length - 1];
          if (last && last.end === s.min) {
            last.end = s.min + 30;
            if (s.reason) last.reasons.add(s.reason);
          } else {
            runs.push({ start: s.min, end: s.min + 30, reasons: new Set(s.reason ? [s.reason] : []) });
          }
        });
        overview[day][idx] = runs;
      });
    });
  }

  const reasonsText = (reasons) => (reasons.size > 0 ? ` · ${[...reasons].join(", ")}` : "");

  // 편집 모드 블록/고스트 렌더링
  const renderEditSegment = (code, seg) => {
    const top = yOf(seg.start) + 1;
    const height = ((seg.end - seg.start) / 30) * HALF_H - 2;
    const range = `${minToTime(seg.start)}~${minToTime(seg.end)}`;

    if (seg.type === "rem") {
      return (
        <Tooltip key={seg.start} title="클릭하면 차단으로 복구됩니다" arrow enterDelay={300}>
          <Box
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => editable && restoreKeys(seg.keys)}
            sx={{
              position: "absolute", left: 3, right: 3, top, height,
              borderRadius: "4px", boxSizing: "border-box", cursor: "pointer",
              backgroundImage: `repeating-linear-gradient(45deg, ${ghostHatch} 0 4px, transparent 4px 8px)`,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5,
              color: ghostText, zIndex: 1
            }}
          >
            <Undo sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1, whiteSpace: "nowrap" }}>
              {range} 해제
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    const isAdd = seg.type === "add";
    const hovered = hoverSeg?.day === code && hoverSeg.min >= seg.start && hoverSeg.min < seg.end;
    const bgLayers = [];
    if (isAdd) bgLayers.push(`repeating-linear-gradient(45deg, ${stripe} 0 4px, transparent 4px 9px)`);
    if (hovered) bgLayers.push(`repeating-linear-gradient(to bottom, transparent 0 ${HALF_H - 1}px, ${stripe} ${HALF_H - 1}px ${HALF_H}px)`);
    return (
      <Tooltip key={seg.start} title={isAdd ? `${range} 추가 예정 — 30분 구간을 클릭하면 취소` : `${range} 차단 중${reasonsText(seg.reasons)} — 30분 구간을 클릭하면 해제`} arrow enterDelay={600}>
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          onMouseMove={(e) => {
            if (!editable) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const idx = Math.min(seg.keys.length - 1, Math.max(0, Math.floor((e.clientY - rect.top) / HALF_H)));
            const min = seg.start + idx * 30;
            setHoverSeg((p) => (p?.day === code && p.min === min ? p : { day: code, min }));
          }}
          onMouseLeave={() => setHoverSeg(null)}
          onClick={(e) => {
            if (!editable) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const idx = Math.min(seg.keys.length - 1, Math.max(0, Math.floor((e.clientY - rect.top) / HALF_H)));
            cutCell(code, seg.start + idx * 30, seg.type);
          }}
          sx={{
            position: "absolute", left: 3, right: 3, top, height,
            borderRadius: "6px", boxSizing: "border-box", overflow: "hidden",
            cursor: editable ? "pointer" : "default",
            bgcolor: selectedPal.fill,
            borderLeft: `3px solid ${selectedPal.bar}`,
            backgroundImage: bgLayers.join(", ") || "none",
            outline: hovered ? `1.5px solid ${selectedPal.bar}` : "none",
            color: selectedPal.text, px: 0.7, pt: "3px", zIndex: 1
          }}
        >
          {height >= 18 && (
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap" }}>
              {range}
            </Typography>
          )}
          {isAdd && (
            <Box sx={{
              position: "absolute", top: 3, right: 3, px: 0.6, py: "1px",
              borderRadius: "8px", bgcolor: selectedPal.bar, color: "#fff",
              fontSize: "0.7rem", fontWeight: 600, lineHeight: 1.4
            }}>
              저장 전
            </Box>
          )}
          {hovered && (
            <Box sx={{
              position: "absolute", left: 0, right: 0,
              top: yOf(hoverSeg.min) - yOf(seg.start), height: HALF_H,
              bgcolor: cutOverlay, display: "flex", alignItems: "center",
              justifyContent: "flex-end", pr: 0.6, pointerEvents: "none"
            }}>
              <ContentCut sx={{ fontSize: 15 }} />
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  };

  // 드래그 중 미리보기 블록
  const renderDragPreview = (code) => {
    if (!drag || drag.day !== code) return null;
    const lo = Math.min(drag.anchor, drag.current);
    const hi = Math.max(drag.anchor, drag.current) + 30;
    return (
      <Box sx={{
        position: "absolute", left: 3, right: 3,
        top: yOf(lo) + 1, height: yOf(hi) - yOf(lo) - 2,
        borderRadius: "6px", boxSizing: "border-box", pointerEvents: "none",
        bgcolor: selectedPal.fill,
        borderLeft: `3px solid ${selectedPal.bar}`,
        backgroundImage: `repeating-linear-gradient(45deg, ${stripe} 0 4px, transparent 4px 9px)`,
        color: selectedPal.text, px: 0.7, pt: "3px", zIndex: 2
      }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap" }}>
          {minToTime(lo)}~{minToTime(hi)}
        </Typography>
      </Box>
    );
  };

  // 전체 보기: 해당 요일에 차단이 있는 선생님끼리 레인을 나눠 나란히 배치
  const renderOverviewDay = (code) => {
    const byIdx = overview[code] || {};
    const laneIdxs = Object.keys(byIdx).map(Number).sort((a, b) => a - b);
    const k = laneIdxs.length;
    if (k === 0) return null;
    return laneIdxs.flatMap((idx, lane) => {
      const p = pal(idx);
      const name = teachers[idx]?.name || "선생님";
      return byIdx[idx].map((run) => {
        const top = yOf(Math.max(run.start, axis.start)) + 1;
        const bottom = yOf(Math.min(run.end, axis.end));
        const height = Math.max(bottom - top - 1, HALF_H - 2);
        const range = `${minToTime(run.start)}~${minToTime(run.end)}`;
        const tall = height >= 40;
        return (
          <Tooltip key={`${idx}-${run.start}`} title={`${name} ${range}${reasonsText(run.reasons)} — 클릭하면 편집`} arrow enterDelay={300}>
            <Box
              onClick={() => selectTeacher(teachers[idx]?.id)}
              sx={{
                position: "absolute",
                left: `calc(${(lane / k) * 100}% + 2px)`,
                width: `calc(${(1 / k) * 100}% - 5px)`,
                top, height,
                borderRadius: "6px", boxSizing: "border-box", overflow: "hidden",
                bgcolor: p.fill, borderLeft: `3px solid ${p.bar}`,
                color: p.text, px: 0.7, pt: "3px", zIndex: 1,
                cursor: "pointer",
                "&:hover": { outline: `1.5px solid ${p.bar}` }
              }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.25, whiteSpace: "nowrap" }}>
                {name}
              </Typography>
              {tall && (
                <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.2, whiteSpace: "nowrap", color: p.bar }}>
                  {k >= 2
                    ? <>{minToTime(run.start)}~<br />{minToTime(run.end)}</>
                    : range}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      });
    });
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
      {/* 선생님 선택 칩 + 사유 입력 */}
      <Stack direction="row" spacing={0.8} alignItems="center" mb={1.5} useFlexGap flexWrap="wrap">
        <Chip
          label="전체 보기"
          clickable
          color={selectedId == null ? "primary" : "default"}
          variant={selectedId == null ? "filled" : "outlined"}
          onClick={() => selectTeacher(null)}
        />
        {teachers.map((t, idx) => {
          const p = pal(idx);
          const active = selectedId === t.id;
          return (
            <Chip
              key={t.id}
              label={`${t.name} 선생님`}
              clickable
              variant={active ? "filled" : "outlined"}
              onClick={() => selectTeacher(t.id)}
              sx={{
                borderColor: p.bar,
                color: active ? "#fff" : p.bar,
                bgcolor: active ? p.bar : "transparent",
                fontWeight: 600,
                "&:hover": { bgcolor: active ? p.bar : undefined }
              }}
            />
          );
        })}
        <Box flex={1} />
        {selectedId == null ? (
          <Typography variant="caption" color="text.secondary">
            선생님을 선택하면 차단 시간을 편집할 수 있습니다.
          </Typography>
        ) : (
          <TextField
            size="small"
            label="새 차단 사유 (선택)"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            sx={{ width: 220 }}
          />
        )}
      </Stack>

      {teachers.length === 0 ? (
        <Alert severity="info">등록된 선생님이 없습니다.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 1.5, userSelect: "none" }}>
          {/* 시간 축 확장 (위) */}
          {axis.start > AXIS_MIN && (
            <Box sx={{ textAlign: "center" }}>
              <Button
                size="small"
                startIcon={<KeyboardArrowUp sx={{ fontSize: 14 }} />}
                onClick={() => setAxis((p) => ({ ...p, start: Math.max(AXIS_MIN, p.start - 60) }))}
                sx={{ fontSize: "0.7rem", py: 0, color: "text.secondary" }}
              >
                이전 시간 보기 ({minToTime(Math.max(AXIS_MIN, axis.start - 60))}~)
              </Button>
            </Box>
          )}

          {/* 요일 헤더 */}
          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: 52, flexShrink: 0 }} />
            {DAY_CODES.map(([code, label]) => (
              <Typography key={code} variant="caption" textAlign="center" fontWeight={600}
                color={code === "SAT" ? "primary.main" : "text.secondary"} sx={{ flex: 1, pb: 0.5, fontSize: "0.85rem" }}>
                {label}
              </Typography>
            ))}
          </Box>

          {/* 캔버스 */}
          <Box sx={{ display: "flex", position: "relative", height: canvasH }}>
            {/* 시간 라벨 */}
            <Box sx={{ width: 52, flexShrink: 0, position: "relative" }}>
              {Array.from({ length: Math.floor((axis.end - axis.start) / 60) + 1 }, (_, i) => {
                const m = axis.start + i * 60;
                if (m > axis.end) return null;
                return (
                  <Typography key={m} variant="caption" color="text.disabled"
                    sx={{ position: "absolute", top: yOf(m) - 8, right: 8, fontSize: "0.75rem" }}>
                    {minToTime(m)}
                  </Typography>
                );
              })}
            </Box>
            {/* 요일 컬럼 */}
            {DAY_CODES.map(([code]) => (
              <Box
                key={code}
                onMouseDown={(e) => {
                  if (!editable || e.button !== 0) return;
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const slot = Math.max(0, Math.min(
                    Math.floor((e.clientY - rect.top) / HALF_H),
                    (axis.end - axis.start) / 30 - 1
                  ));
                  const min = axis.start + slot * 30;
                  setDrag({ day: code, rectTop: rect.top, anchor: min, current: min });
                }}
                sx={{
                  flex: 1, position: "relative", minWidth: 0,
                  borderLeft: "1px solid", borderColor: "divider",
                  cursor: editable ? "crosshair" : "default",
                  ...colBg
                }}
              >
                {selectedId != null
                  ? (segByDay[code] || []).map((seg) => renderEditSegment(code, seg))
                  : renderOverviewDay(code)}
                {renderDragPreview(code)}
              </Box>
            ))}
          </Box>

          {/* 시간 축 확장 (아래) */}
          {axis.end < AXIS_MAX && (
            <Box sx={{ textAlign: "center" }}>
              <Button
                size="small"
                startIcon={<KeyboardArrowDown sx={{ fontSize: 14 }} />}
                onClick={() => setAxis((p) => ({ ...p, end: Math.min(AXIS_MAX, p.end + 60) }))}
                sx={{ fontSize: "0.7rem", py: 0, color: "text.secondary" }}
              >
                이후 시간 보기 (~{minToTime(Math.min(AXIS_MAX, axis.end + 60))})
              </Button>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.8 }}>
            {selectedId == null
              ? "매주 반복되는 회원 예약 차단 시간입니다. 겹치는 시간대는 선생님별로 나란히 표시됩니다."
              : "빈 곳을 드래그하면 새 차단이 그려집니다. 블록 위에서 30분 구간을 클릭하면 그 구간만 해제되고, 회색 빗금을 클릭하면 복구됩니다. 사유 입력값은 이번 저장에서 새로 추가되는 차단에 적용됩니다."}
          </Typography>
        </Paper>
      )}

      {/* 저장 바: 변경 목록 칩 + 일괄 저장 */}
      {dirty && (
        <Box sx={{
          position: "sticky", bottom: 0, mt: 2, mx: -2, px: 2, py: 1,
          bgcolor: "background.paper", borderTop: 1, borderColor: "divider",
          display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", zIndex: 5
        }}>
          <Typography variant="caption" fontWeight={600} sx={{ mr: 0.5 }}>
            저장 전 변경 {changes.length}건
          </Typography>
          {changes.map((ch) => (
            <Chip
              key={`${ch.day}-${ch.start}-${ch.type}`}
              size="small"
              variant="outlined"
              label={`${DAY_LABEL[ch.day]} ${minToTime(ch.start)}~${minToTime(ch.end)} ${ch.type === "add" ? "추가" : "해제"}`}
              onDelete={() => undoChange(ch)}
              deleteIcon={<Undo sx={{ fontSize: 14 }} />}
              sx={{ fontSize: "0.78rem" }}
            />
          ))}
          <Box flex={1} />
          <Button size="small" onClick={resetCells} disabled={saving}>모두 되돌리기</Button>
          <Button size="small" variant="contained" onClick={save} disabled={saving}>
            {saving ? "저장 중..." : "저장"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
